import React from 'react';
import { withRouter } from 'react-router-dom';
import styled from 'styled-components';
import { ArrowBack as ArrowBackIcon, NavigateNext } from '@material-ui/icons';
import { Tooltip } from '@material-ui/core';
import { lightTheme as theme } from '../../theme';

// Layout Container
const LayoutContainer = styled.div`
    min-height: 100vh;
    background-color: ${theme.colors.background};
    display: flex;
    flex-direction: column;
`;

// New Header Style (replacing the old transparent one)
const HeaderContainer = styled.div`
    background-color: ${theme.colors.primary}; /* Sober color requested (Dark Blue) */
    padding: 24px 32px;
    display: flex;
    flex-direction: column;
    gap: 8px;
    color: ${theme.colors.headerText};
    box-shadow: ${theme.colors.shadowSm};
`;

const BreadcrumbNav = styled.nav`
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 8px;
    font-size: 14px;
    color: ${theme.colors.headerTextMuted};

    a, button {
        color: ${theme.colors.headerTextMuted};
        text-decoration: none;
        background: none;
        border: none;
        padding: 0;
        font: inherit;
        cursor: pointer;
        opacity: 0.9;
        
        &:hover {
            color: ${theme.colors.headerText};
            text-decoration: underline;
        }

        &:disabled {
            cursor: default;
            text-decoration: none;
            opacity: 1;
            color: ${theme.colors.headerText};
        }
    }

    button:disabled {
        font-weight: 600;
    }
`;

const Separator = styled(NavigateNext)`
    font-size: 16px !important;
    opacity: 0.7;
`;

const PageTitleWrapper = styled.div`
    display: flex;
    align-items: center;
    gap: 16px;
    margin-top: 4px;
`;

const BackButton = styled.button`
    background: rgba(255, 255, 255, 0.1);
    border: none;
    border-radius: 50%;
    width: 36px;
    height: 36px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
    cursor: pointer;
    transition: background 0.2s;

    &:hover {
        background: rgba(255, 255, 255, 0.2);
    }
`;

const Title = styled.h1`
    font-size: 24px;
    font-weight: 700;
    margin: 0;
    color: ${theme.colors.headerText};
`;

const ContentWrapper = styled.div`
    padding: 24px 32px;
    width: 100%;
    max-width: 1600px;
    margin: 0 auto;
    flex: 1;

    @media (max-width: 768px) {
        padding: 16px;
    }
`;

class BreadcrumbLayoutComponent extends React.Component {
    handleBackClick = () => {
        const { breadcrumbItems, history, onBackClick } = this.props;

        if (onBackClick) {
            onBackClick();
            return;
        }

        // Logic: Go to the second to last item if available
        if (breadcrumbItems && breadcrumbItems.length >= 2) {
            const previousItem = breadcrumbItems[breadcrumbItems.length - 2];
            if (previousItem.onClick) {
                previousItem.onClick();
                return;
            }
            if (previousItem.path) {
                history.push(previousItem.path);
                return;
            }
        }

        history.goBack();
    };

    handleItemClick = (item, index) => {
        const { breadcrumbItems, history } = this.props;
        if (index === breadcrumbItems.length - 1) return; // Current page

        if (item.onClick) {
            item.onClick();
        } else if (item.path) {
            history.push(item.path);
        }
    };

    render() {
        const { breadcrumbItems, title, showBackButton = true, currentLanguage, children } = this.props;

        return (
            <LayoutContainer>
                <HeaderContainer>
                    {breadcrumbItems && breadcrumbItems.length > 0 && (
                        <BreadcrumbNav aria-label="breadcrumb">
                            {breadcrumbItems.map((item, index) => {
                                const isLast = index === breadcrumbItems.length - 1;
                                return (
                                    <React.Fragment key={index}>
                                        {index > 0 && <Separator />}
                                        <button
                                            type="button"
                                            disabled={isLast} // Disabled uses the "current page" style
                                            onClick={() => this.handleItemClick(item, index)}
                                        >
                                            {item.label}
                                        </button>
                                    </React.Fragment>
                                );
                            })}
                        </BreadcrumbNav>
                    )}

                    <PageTitleWrapper>
                        {showBackButton && (
                            <Tooltip title={currentLanguage?.bepiService?.shared?.shared_breadcrumb_back_btn || "Voltar"}>
                                <BackButton onClick={this.handleBackClick}>
                                    <ArrowBackIcon />
                                </BackButton>
                            </Tooltip>
                        )}
                        <Title>{title}</Title>
                    </PageTitleWrapper>
                </HeaderContainer>

                <ContentWrapper>
                    {children}
                </ContentWrapper>
            </LayoutContainer>
        );
    }
}

export default withRouter(BreadcrumbLayoutComponent);
